const CONNECTIVITY_TIMEOUT_MS = 5000;
const DEFAULT_MAX_RETRIES = 10;
const BACKOFF_DELAYS_MS = [1000, 2000, 3000, 4000, 5000, 5000, 5000, 5000, 5000, 5000];

export class NetworkMonitor {
    private listeners: ((isOnline: boolean) => void) | null = null;
    private isOnline = navigator.onLine;

    constructor() {
        window.addEventListener('online', () => this.handleOnlineStatusChange(true));
        window.addEventListener('offline', () => this.handleOnlineStatusChange(false));
    }

    /**
     * Get current online status
     */
    getStatus(): boolean {
        return this.isOnline;
    }

    /**
     * Sync internal status from navigator.onLine (fixes stale boot-time state)
     */
    syncFromNavigator(): boolean {
        const online = navigator.onLine;
        this.isOnline = online;
        return online;
    }

    /**
     * Force update online status and notify listeners when value changes
     */
    setStatus(isOnline: boolean): void {
        this.handleOnlineStatusChange(isOnline);
    }

    /**
     * Actual network connectivity check via HTTP request (not just navigator.onLine)
     */
    async checkActualConnectivity(): Promise<boolean> {
        if (!navigator.onLine) {
            return false;
        }

        const baseUrl = import.meta.env.VITE_API_INTERNAL_URL || '/';
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), CONNECTIVITY_TIMEOUT_MS);

        try {
            // no-cors: opaque response still means the network path is reachable
            await fetch(baseUrl, {
                method: 'HEAD',
                cache: 'no-store',
                mode: 'no-cors',
                signal: controller.signal,
            });
            return true;
        } catch {
            return false;
        } finally {
            clearTimeout(timeoutId);
        }
    }

    /**
     * Wait for network with exponential backoff
     */
    async waitForNetwork(maxRetries = DEFAULT_MAX_RETRIES, initialDelay = 1000): Promise<boolean> {
        for (let i = 0; i < maxRetries; i++) {
            const isConnected = await this.checkActualConnectivity();

            if (isConnected) {
                this.handleOnlineStatusChange(true);
                return true;
            }

            // Publish offline on the first failed probe so the no-cache boot
            // screen can leave 0/3 while the remaining retries continue.
            this.handleOnlineStatusChange(false);

            const delay = BACKOFF_DELAYS_MS[i] ?? initialDelay;
            await new Promise((resolve) => setTimeout(resolve, delay));
        }

        this.handleOnlineStatusChange(false);
        return false;
    }

    /**
     * Add a listener for online status changes
     */
    addListener(listener: (isOnline: boolean) => void) {
        this.listeners = listener;
    }

    /**
     * Remove listener
     */
    removeListener() {
        this.listeners = null;
    }

    /**
     * Check if should fetch today (only once per day)
     */
    shouldFetchToday(): boolean {
        const today = new Date().toDateString();
        const lastFetch = sessionStorage.getItem('lastConfigFetch');

        if (lastFetch !== today) {
            return true;
        }

        return false;
    }

    private handleOnlineStatusChange(isOnline: boolean) {
        if (this.isOnline !== isOnline) {
            this.isOnline = isOnline;
            if (this.listeners) {
                this.listeners(isOnline);
            }
        }
    }
}

export const networkMonitor = new NetworkMonitor();
